// Dashboard — painel de comando do gestor. A pergunta que ele responde nos
// primeiros segundos é "a operação está rodando AGORA?", não "quantas ligações
// deram no mês". Por isso a tela é lida de cima para baixo em três camadas:
//
//   1. Agora    — equipe, chamadas ao vivo e fila. Sem período, recarrega sozinho.
//   2. Hoje     — ritmo por hora contra o mesmo horário de ontem.
//   3. Período  — o retrospecto, aí sim com o seletor de datas.
//
// O recorte é a operação INTEIRA (discador + click-to-call). Quem quer só o
// discador tem o Relatório geral, que corta por is_predictive.
//
// Os blocos que dependem de dado da organização toda (equipe, fila) só existem
// para gestor: as rotas por trás deles são requireManager no back, e pedi-las
// como vendedor daria 403. A conta é pura, mora em dashboard-model.ts.
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.js";
import { Link } from "@tanstack/react-router";
import type { DateRange } from "react-day-picker";
import { subDays } from "date-fns";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  PhoneOff,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { type CallStatus } from "./api.js";
import {
  useActiveCalls,
  useDashboardCallsSummary,
  useDashboardConversionsSummary,
  usePaceCallsSummary,
} from "@/hooks/use-calls";
import { useDashboardAgentAvailability, useDashboardQueueTotals } from "@/hooks/use-dialer";
import { useLeadStatuses } from "@/hooks/use-leads";
import { StatusBadge } from "./calls-common.js";
import { formatDuration } from "./sip/softphone-status.js";
import { fillPerDay, overallAvgDuration } from "./lib/call-metrics.js";
import { idleAgents, queueColumns, todayPace } from "./dashboard-model.js";
import { isManager } from "@/lib/access";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";
import { DateRangePicker } from "@/components/date-range-picker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Gráficos (recharts) carregados sob demanda: o recharts (~102 KB gzip) fica
// fora do bundle inicial, então a camada "Agora" pinta primeiro e os gráficos
// entram logo em seguida. O fallback preserva a altura para não haver salto.
const CallsPerDayChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({
    default: m.CallsPerDayChart,
  })),
);
const AvgDurationChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({
    default: m.AvgDurationChart,
  })),
);
const TodayPaceChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({ default: m.TodayPaceChart })),
);

function ChartFallback({ height = 220 }: { height?: number }) {
  return <Skeleton style={{ height }} className="w-full" />;
}

// De quanto em quanto tempo a camada "Agora" se atualiza. 10s é o mesmo ritmo
// da tela de Chamadas em andamento — o gestor vê os dois lado a lado.
const LIVE_REFETCH_MS = 10_000;
// Fila e ritmo mudam devagar; recarregar a cada 10s só gastaria consulta.
const SLOW_REFETCH_MS = 60_000;
// Quantos status da fila cabem no cartão antes de virar "+ N em outros".
const QUEUE_COLUMNS = 4;
// Quantos parados aparecem nominalmente. Mais que isso vira lista, não alarme.
const IDLE_NAMES = 3;

// Ordem de exibição do resumo por status (previsível, independe do back).
const STATUS_ORDER: CallStatus[] = [
  "COMPLETED",
  "IN_PROGRESS",
  "RINGING",
  "NO_ANSWER",
  "FAILED",
];

// Início do dia (from) e fim do dia (to) em ISO, no padrão do repo.
function toIso(range: DateRange): { from: string; to: string } {
  const from = new Date(range.from!);
  from.setHours(0, 0, 0, 0);
  const to = new Date(range.to ?? range.from!);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

// Ontem 00:00 até agora — a janela do "ritmo de hoje" precisa dos dois dias na
// mesma resposta para comparar hora a hora.
function paceWindow(now: Date): { from: string; to: string } {
  const from = subDays(now, 1);
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: now.toISOString() };
}

// "1:02:03" para o tempo parado do vendedor: a mesma formatação de duração do
// resto do app.
function idleLabel(seconds: number): string {
  return formatDuration(seconds);
}

function KpiCard({
  title,
  value,
  hint,
  icon: Icon,
  accent,
}: {
  title: string;
  value: string | number;
  hint?: string;
  icon: typeof Phone;
  accent: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`size-4 ${accent}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

// Cartão da camada "Agora": título, um link para a tela que aprofunda o assunto
// e o conteúdo. O link é o que impede o dashboard de virar relatório — aqui só
// mora o sinal, o detalhe fica na tela dona do assunto.
function LiveCard({
  title,
  to,
  linkLabel,
  children,
}: {
  title: string;
  to: string;
  linkLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Link
          to={to}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {linkLabel}
          <ArrowRight className="size-3" />
        </Link>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function Dashboard() {
  const { t } = useTranslation();
  // Período do retrospecto: últimos 7 dias (hoje inclusive). Vale só para a
  // terceira camada — as duas de cima são sempre "agora" e "hoje".
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const today = new Date();
    return { from: subDays(today, 6), to: today };
  });

  // Relógio da tela. O tempo parado do vendedor e a hora atual do ritmo mudam
  // sozinhos; sem este tique a tela envelheceria em silêncio entre refetches.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), LIVE_REFETCH_MS);
    return () => clearInterval(id);
  }, []);

  // Papel-base só para o gate dos blocos de organização (equipe/fila) — vem do
  // cache do bootstrap (/v1/access/me), sem precisar da lista de membros.
  const { baseRole } = useAccessSnapshot();
  const canSeeOrg = isManager(baseRole);

  const hasRange = !!range?.from;
  const iso = useMemo(
    () => (hasRange ? toIso(range!) : null),
    [hasRange, range],
  );

  // --- Camada 1: agora ---
  const activeQuery = useActiveCalls({ refetchInterval: LIVE_REFETCH_MS });
  const agentsQuery = useDashboardAgentAvailability({
    enabled: canSeeOrg,
    refetchInterval: LIVE_REFETCH_MS,
  });
  const queueQuery = useDashboardQueueTotals({
    enabled: canSeeOrg,
    refetchInterval: SLOW_REFETCH_MS,
  });
  const statusesQuery = useLeadStatuses({ enabled: canSeeOrg });

  // --- Camada 2: hoje ---
  const paceIso = useMemo(() => paceWindow(now), [now]);
  const paceQuery = usePaceCallsSummary(
    paceIso.from.slice(0, 13),
    () => paceWindow(new Date()),
    { refetchInterval: SLOW_REFETCH_MS },
  );

  // --- Camada 3: período ---
  const summaryQuery = useDashboardCallsSummary(iso, { enabled: hasRange });
  const conversionsQuery = useDashboardConversionsSummary(iso, { enabled: hasRange });

  const activeCount = activeQuery.data?.calls.length ?? 0;
  const agents = agentsQuery.data ?? [];
  const inCall = agents.filter((a) => a.inCall).length;
  const available = agents.filter(
    (a) => a.status === "AVAILABLE" && !a.inCall,
  ).length;
  const offDuty = agents.length - inCall - available;
  const idle = useMemo(() => idleAgents(agents, now), [agents, now]);

  const queue = useMemo(
    () =>
      queueColumns(
        queueQuery.data?.totals ?? [],
        statusesQuery.data ?? [],
        QUEUE_COLUMNS,
      ),
    [queueQuery.data, statusesQuery.data],
  );

  const pace = useMemo(
    () => todayPace(paceQuery.data?.perHour ?? [], now),
    [paceQuery.data, now],
  );

  const summary = summaryQuery.data;
  const total = summary?.totalCalls ?? 0;
  const completed =
    summary?.byStatus.find((s) => s.status === "COMPLETED")?.count ?? 0;
  const rate = total > 0 ? `${Math.round((completed / total) * 100)}%` : "—";
  const avgSec = summary ? overallAvgDuration(summary) : null;
  const conversionRate = conversionsQuery.data?.totals.rate ?? null;
  const conversions = conversionsQuery.data?.totals.conversions ?? 0;

  const statusMap = new Map(summary?.byStatus.map((s) => [s.status, s.count]));
  const series = useMemo(
    () =>
      range?.from
        ? fillPerDay(summary?.perDay ?? [], range.from, range.to ?? range.from)
        : [],
    [summary, range],
  );
  const hasAvgSeries = series.some((d) => d.avgSec != null);

  return (
    <main className="p-6">
      <div className="mb-6">
        <h1 className="text-lg font-semibold tracking-tight">
          {t("dashboard.title")}
        </h1>
      </div>

      {/* --- Agora --------------------------------------------------------- */}
      <div className="grid gap-4 lg:grid-cols-3">
        {canSeeOrg && (
          <LiveCard
            title={t("dashboard.live.team")}
            to="/monitoring/active-calls"
            linkLabel={t("dashboard.live.viewTeam")}
          >
            {agentsQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : agents.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("dashboard.live.noSellers")}
              </p>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold tabular-nums">
                    {inCall}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t("dashboard.live.teamSummary", {
                      inCall,
                      available,
                      offDuty,
                    })}
                  </span>
                </div>
                {idle.length > 0 ? (
                  <ul className="mt-3 space-y-1">
                    {idle.slice(0, IDLE_NAMES).map((agent) => (
                      <li
                        key={agent.userId}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="truncate text-muted-foreground">
                          {agent.name}
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {t("dashboard.live.idleFor", {
                            duration: idleLabel(agent.seconds),
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">
                    {t("dashboard.live.noIdle")}
                  </p>
                )}
              </>
            )}
          </LiveCard>
        )}

        <LiveCard
          title={t("dashboard.live.calls")}
          to="/monitoring/active-calls"
          linkLabel={t("dashboard.live.open")}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold tabular-nums">
              {activeCount}
            </span>
            <span className="text-sm text-muted-foreground">
              {t(
                activeCount === 1
                  ? "dashboard.live.activeCalls_one"
                  : "dashboard.live.activeCalls_other",
              )}
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {t("dashboard.live.autoRefresh")}
          </p>
        </LiveCard>

        {canSeeOrg && (
          <LiveCard
            title={t("dashboard.live.queue")}
            to="/management/queue-kanban"
            linkLabel={t("dashboard.live.viewBoard")}
          >
            {queueQuery.isLoading || statusesQuery.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : queue.total === 0 ? (
              <div className="text-sm text-muted-foreground">
                <p>{t("dashboard.live.emptyQueue")}</p>
                <p className="mt-1">
                  {t("dashboard.live.unmappedBefore")}{" "}
                  <Link
                    to="/management/lead-statuses"
                    className="underline hover:text-foreground"
                  >
                    {t("dashboard.live.checkStatusMap")}
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold tabular-nums">
                    {queue.total}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t("dashboard.live.waitingLeads", { count: queue.total })}
                  </span>
                </div>
                <ul className="mt-3 space-y-1">
                  {queue.columns.map((column) => (
                    <li
                      key={column.key}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: column.color }}
                        />
                        <span className="truncate text-muted-foreground">
                          {column.label}
                        </span>
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {column.total}
                      </span>
                    </li>
                  ))}
                  {queue.rest > 0 && (
                    <li className="text-xs text-muted-foreground">
                      {t("dashboard.live.otherStatuses", { count: queue.rest })}
                    </li>
                  )}
                </ul>
              </>
            )}
          </LiveCard>
        )}
      </div>

      {/* --- Hoje ---------------------------------------------------------- */}
      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("dashboard.pace.title")}
          </CardTitle>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-2xl font-semibold tabular-nums">
              {pace.today}
            </span>
            <span className="text-sm text-muted-foreground">
              {t(
                pace.today === 1
                  ? "dashboard.pace.callsToday_one"
                  : "dashboard.pace.callsToday_other",
              )}
            </span>
            {pace.deltaPct != null && (
              <span
                className={`flex items-center gap-1 text-sm tabular-nums ${
                  pace.deltaPct >= 0
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-amber-700 dark:text-amber-500"
                }`}
              >
                {pace.deltaPct >= 0 ? (
                  <TrendingUp className="size-4" />
                ) : (
                  <TrendingDown className="size-4" />
                )}
                {pace.deltaPct > 0 ? "+" : ""}
                {t("dashboard.pace.vsYesterday", {
                  percent: pace.deltaPct,
                  count: pace.yesterdayToNow,
                })}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {paceQuery.isLoading ? (
            <ChartFallback height={200} />
          ) : pace.today === 0 && pace.yesterdayToNow === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {t("dashboard.pace.empty")}
            </p>
          ) : (
            <Suspense fallback={<ChartFallback height={200} />}>
              <TodayPaceChart data={pace.series} />
            </Suspense>
          )}
        </CardContent>
      </Card>

      {/* --- Período ------------------------------------------------------- */}
      <div className="mt-8 mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">
            {t("dashboard.period.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("dashboard.period.description")}
          </p>
        </div>
        <DateRangePicker
          value={range}
          onChange={setRange}
          className="sm:w-auto"
        />
      </div>

      {summaryQuery.error && (
        <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {t("dashboard.period.loadError", {
            message: (summaryQuery.error as Error).message,
          })}
        </div>
      )}

      {summaryQuery.isLoading ? (
        <div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
          aria-busy="true"
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-28 w-full" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <KpiCard
              title={t("dashboard.metrics.totalCalls")}
              value={total}
              hint={t("dashboard.metrics.selectedPeriod")}
              icon={Phone}
              accent="text-muted-foreground"
            />
            <KpiCard
              title={t("dashboard.metrics.answered")}
              value={completed}
              hint={t("dashboard.metrics.completedCalls")}
              icon={CheckCircle2}
              accent="text-emerald-600 dark:text-emerald-400"
            />
            <KpiCard
              title={t("dashboard.metrics.answerRate")}
              value={rate}
              hint={t("dashboard.metrics.answerRateHint")}
              icon={PhoneOff}
              accent="text-muted-foreground"
            />
            <KpiCard
              title={t("dashboard.metrics.averageTime")}
              value={avgSec != null ? formatDuration(avgSec) : "—"}
              hint={t("dashboard.metrics.averageTimeHint")}
              icon={Timer}
              accent="text-violet-600 dark:text-violet-400"
            />
            <KpiCard
              title={t("dashboard.metrics.successRate")}
              value={conversionRate == null ? "—" : `${conversionRate}%`}
              hint={t("dashboard.metrics.conversions", { count: conversions })}
              icon={Target}
              accent="text-emerald-600 dark:text-emerald-400"
            />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {/* Progressão por dia — total de chamadas. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  {t("dashboard.charts.callsPerDay")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {total === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t("dashboard.charts.noCalls")}
                  </p>
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <CallsPerDayChart data={series} />
                  </Suspense>
                )}
              </CardContent>
            </Card>

            {/* Tempo médio por dia. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  {t("dashboard.charts.averagePerDay")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!hasAvgSeries ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t("dashboard.charts.noAnswered")}
                  </p>
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <AvgDurationChart data={series} />
                  </Suspense>
                )}
              </CardContent>
            </Card>
          </div>

          {summary && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  {t("dashboard.status.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {total === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t("dashboard.status.empty")}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {STATUS_ORDER.filter(
                      (s) => (statusMap.get(s) ?? 0) > 0,
                    ).map((status) => {
                      const count = statusMap.get(status) ?? 0;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <li key={status} className="flex items-center gap-3">
                          <div className="w-32 shrink-0">
                            <StatusBadge status={status} />
                          </div>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-16 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                            {count} · {pct}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </main>
  );
}
